import React from 'react'
import Icon, { type IconName } from '../icons/Icon'
import InstitutionMark from './InstitutionMark'
import type {
  LinkedAccountsResponse,
  PlaidInstitution,
  PlaidAccount,
} from '../../services/api'

interface LinkedInstitutionsSectionProps {
  linkedData: LinkedAccountsResponse | null
  institutions: PlaidInstitution[]
  isRefreshing: boolean
  isLinking: boolean
  isLoading: boolean
  onReconnect: (institution: PlaidInstitution) => void
  onDisconnect: (institution: PlaidInstitution) => void
}

/**
 * Format a numeric balance for display.
 */
const formatBalance = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return 'N/A'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)
}

/**
 * Build a human-readable account label from subtype or type.
 */
const getAccountTypeLabel = (account: PlaidAccount): string => {
  const subtype = account.subtype || account.type
  return subtype.charAt(0).toUpperCase() + subtype.slice(1).replace(/_/g, ' ')
}

// Credit cards and loans hold balances that are debts, not assets, so they
// subtract from aggregate totals (handled server-side) and render as liabilities.
const isLiabilityAccount = (account: PlaidAccount): boolean =>
  ['credit', 'loan'].includes(account.type.toLowerCase())

/**
 * Choose an icon for a Plaid account type.
 */
const getAccountIcon = (type: string): IconName => {
  switch (type.toLowerCase()) {
    case 'depository':
      return 'vault'
    case 'credit':
      return 'card'
    case 'loan':
      return 'receipt'
    case 'investment':
      return 'trending'
    default:
      return 'wallet'
  }
}

/**
 * Presentational section that renders the linked-institution summary, including
 * aggregate stats and per-institution account cards. All Plaid orchestration and
 * state stays in the parent; this component receives data and callbacks as props.
 */
const LinkedInstitutionsSection: React.FC<LinkedInstitutionsSectionProps> = ({
  linkedData,
  institutions,
  isRefreshing,
  isLinking,
  isLoading,
  onReconnect,
  onDisconnect,
}) => {
  return (
    <div className="linked-accounts-section">
      <div className="linked-header">
        <h2>Your Linked Institutions</h2>
        <div className="linked-header-meta">
          {isRefreshing && (
            <span className="refreshing-indicator" role="status">
              Updating balances…
            </span>
          )}
          <span className="institution-badge">
            <Icon name="layers" />
            {linkedData?.institution_count ?? 0} linked
          </span>
        </div>
      </div>

      <div className="summary-grid">
        <div className="total-balance-card">
          <span className="balance-label">Aggregate Balance</span>
          <span className="balance-amount">
            {formatBalance(linkedData?.total_balance)}
          </span>
        </div>
        <div className="summary-stat-card">
          <span className="summary-stat-label">Institution Count</span>
          <span className="summary-stat-value">{linkedData?.institution_count ?? 0}</span>
        </div>
        <div className="summary-stat-card">
          <span className="summary-stat-label">Account Count</span>
          <span className="summary-stat-value">{linkedData?.account_count ?? 0}</span>
        </div>
      </div>

      <div className="institution-list">
        {institutions.map((institution) => (
          <div key={institution.plaid_item_id} className="institution-card">
            <div className="institution-card-header">
              <div className="institution-identity">
                <InstitutionMark name={institution.institution_name} />
                <div>
                <h3>{institution.institution_name || 'Linked institution'}</h3>
                <p className="institution-meta">
                  Status: {institution.status.replace(/_/g, ' ')}
                  {institution.last_error_code ? ` • ${institution.last_error_code}` : ''}
                </p>
                </div>
              </div>
              <div className="institution-actions">
                {institution.needs_reconnection && (
                  <button
                    onClick={() => onReconnect(institution)}
                    disabled={isLinking || isLoading}
                    className="reconnect-btn"
                  >
                    Reconnect
                  </button>
                )}
                <button
                  onClick={() => onDisconnect(institution)}
                  disabled={isLoading}
                  className="disconnect-btn"
                >
                  Disconnect
                </button>
              </div>
            </div>

            <div className="institution-summary-row">
              <span>{institution.account_count} accounts</span>
              <span>Total {formatBalance(institution.total_balance)}</span>
            </div>

            {institution.message && (
              <p className="institution-warning">{institution.message}</p>
            )}

            <div className="accounts-list">
              {institution.accounts.map((account, index) => {
                const liability = isLiabilityAccount(account)
                return (
                  <div
                    key={`${institution.plaid_item_id}-${account.mask ?? 'maskless'}-${index}`}
                    className={`account-card${liability ? ' account-card--liability' : ''}`}
                  >
                    <span className="account-icon">
                      <Icon name={getAccountIcon(account.type)} />
                    </span>
                    <div className="account-info">
                      <h3>{account.name}</h3>
                      <p className="account-type">
                        {getAccountTypeLabel(account)}
                        {account.mask ? ` •••• ${account.mask}` : ''}
                      </p>
                      {liability && <span className="liability-tag">Liability</span>}
                    </div>
                    <div className="account-balances">
                      {liability ? (
                        <div className="balance-row">
                          <span className="balance-label">Balance owed</span>
                          <span className="balance-value balance-value--liability">
                            {account.balances.current !== null && account.balances.current !== undefined
                              ? `−${formatBalance(account.balances.current)}`
                              : 'N/A'}
                          </span>
                        </div>
                      ) : (
                        <>
                          <div className="balance-row">
                            <span className="balance-label">Available</span>
                            <span className="balance-value">
                              {formatBalance(account.balances.available)}
                            </span>
                          </div>
                          <div className="balance-row">
                            <span className="balance-label">Current</span>
                            <span className="balance-value">
                              {formatBalance(account.balances.current)}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default LinkedInstitutionsSection
